import { Link } from '@change/ds-core-ui';
import type { LinkProps } from '@change/ds-core-ui';

import type { CreateLinkReplacer, TranslateHtmlReplaceFn } from '@change-corgi/core-react/i18n';

type Options = Pick<LinkProps, 'variant' | 'size' | 'className' | 'trailingIcon'> & {
	onClick?: () => void;
};

/**
 * Builds a `replace` function for `parseHtml`/`TranslateHtml`/`translateHtml` that swaps a plain
 * <a> parsed from translated copy for the design-system Link, instead of leaving it as bare,
 * unstyled markup. Forwards whatever attributes survive HTML sanitization (target, rel,
 * data-*, ...) rather than hand-picking a few - i18n copy can carry attributes (e.g.
 * data-pass-thru, used to bypass a cookie-consent-gating script) that matter beyond styling.
 *
 * By default the link inherits the font and color of the text around it (Link's `inherit` size
 * and variant), since that copy is usually a sentence with an inline link (e.g. small grey legal
 * text) whose size and color aren't known here. Pass `size`/`variant` to override.
 */
export function createAnchorToLinkReplacer({
	variant = 'inherit',
	size = 'inherit',
	...options
}: Options = {}): TranslateHtmlReplaceFn {
	return ({ name, attribs, children }, { domToReact }) => {
		if (name !== 'a') return undefined;

		const { href, class: cssClass, style, ...rest } = attribs;

		// A missing href would otherwise fall through to the Link's own "#" default, rendering a
		// dead, clickable link where the source markup had none - render the text plainly instead.
		if (!href) return <>{domToReact(children)}</>;

		return (
			<Link {...rest} href={href} variant={variant} size={size} {...options}>
				{domToReact(children)}
			</Link>
		);
	};
}

/**
 * App-wide default for translated copy (set up via `I18nHtmlProvider` in the app root), so every <a> in
 * `TranslateHtml`/`translateHtml` output renders as the design-system Link, calling the call's `onLinkClick` if any
 */
export const createTranslatedLinkReplacer: CreateLinkReplacer = ({ onLinkClick }) =>
	createAnchorToLinkReplacer({ onClick: onLinkClick });
